/* @flow */

import type { $AxiosXHR } from 'axios';
import type { $Activity } from '../types/Activity';
import type { $LanguageTemplate } from '../types/Template';
import type { $ProductGroup } from '../types/ProductGroup';
import type { $Translation } from '../types/Translation';
import ActivityTypeGroups from '../constants/ActivityTypeGroups';
import CMSConsumer from '../consumers/CMSConsumer';
import DMSConsumer from '../consumers/DMSConsumer';
import Globals from '../constants/Globals';
import TemplateTranslations from './../templates/TemplateTranslations';

export const getLanguageTranslations = async (
  cmsURL: string, dmsURL: string, languageId: string
): Promise<Array<$Translation>> => {

  const cms = new CMSConsumer(cmsURL);
  const dms = new DMSConsumer(dmsURL);

  const translationsResponse: $AxiosXHR<Array<$Translation>> = await cms.listTranslations(languageId);
  const activitiesResponse: $AxiosXHR<Array<$Activity>> = await dms.getActivities();
  const productGroupsResponse: $AxiosXHR<Array<$ProductGroup>> = await dms.getProductGroups();

  const templateTranslations: Array<$Translation> = getLanguageTemplateTranslations(translationsResponse.data);

  const activitiesTranslations: Array<$Translation> = getActivityTypesTranslations(
    [ ...ActivityTypeGroups, ...activitiesResponse.data ], translationsResponse.data);

  const productGroupsTranslations: Array<$Translation> = getProductGroupTranslations(
    productGroupsResponse.data, translationsResponse.data);

  return [
    ...templateTranslations,
    ...activitiesTranslations,
    ...productGroupsTranslations
  ];
};

export const getActivityTypeTranslationCode = (code: string) => {
  return `${Globals.TPL_LANGUAGE_ACTIVITY_TYPE_PREFIX}${code}${Globals.TPL_LANGUAGE_ACTIVITY_TYPE_POSTFIX}`;
};

export const getProductGroupTranslationCode = (code: string) => {
  return `${Globals.TPL_LANGUAGE_PRODUCT_GROUP_PREFIX}${code}${Globals.TPL_LANGUAGE_PRODUCT_GROUP_POSTFIX}`;
};

export const getActivityTypeDealerDynamicFieldKey = (code: string) => {
  return `${Globals.DEALER_DYNAMIC_FIELD_PREFIX_ACTIVITY_TYPE}${code}`;
};

const getLanguageTemplateTranslations = (translations: Array<$Translation>): Array<$Translation> => {
  const templateTranslations: Array<$Translation> = [];
  TemplateTranslations.forEach((templateRow: $LanguageTemplate) => {

    const translation = translations && translations.find((translation: $Translation) => {
      if (translation && translation.key) return translation.key === templateRow.key;
    });

    const mappedTranslation: $Translation = {
      key: templateRow.key,
      name: templateRow.name,
      defaultTranslation: templateRow.defaultTranslation || '',
      translation: translation && translation.translation || ''
    };
    templateTranslations.push(mappedTranslation);
  });
  return templateTranslations;
};

const getActivityTypesTranslations = (
  activities: Array<$Activity>, translations: Array<$Translation>
): Array<$Translation> => {
  const activitiesTranslations: Array<$Translation> = [];
  activities.forEach((activity: $Activity) => {

    if (activity && activity.code && activity.name) {

      const { code: activityCode, name: activityName } = activity;
      const activityTypeTranslationCode = getActivityTypeTranslationCode(activityCode);

      const activityTranslation: ?$Translation = translations && translations.find((translation: $Translation) => {
        if (translation && translation.key) return translation.key === activityTypeTranslationCode;
      });

      const mappedTranslation: $Translation = {
        key: activityTypeTranslationCode,
        name: activity.code,
        defaultTranslation: activityName,
        translation: activityTranslation && activityTranslation.translation || ''
      };
      activitiesTranslations.push(mappedTranslation);
    }
  });
  return activitiesTranslations;
};

const getProductGroupTranslations = (
  productGroups: Array<$ProductGroup>, translations: Array<$Translation>
) => {
  const productGroupsTranslations: Array<$Translation> = [];
  productGroups.forEach((productGroup: $ProductGroup) => {

    if (productGroup && productGroup.code && productGroup.name) {

      const { code: productGroupCode, name: productGroupName } = productGroup;
      const productGroupTranslationCode = getProductGroupTranslationCode(productGroupCode);

      const productGroupTranslation: ?$Translation = translations && translations.find((translation: $Translation) => {
        if (translation && translation.key) return translation.key === productGroupTranslationCode;
      });

      const mappedTranslation: $Translation = {
        key: productGroupTranslationCode,
        name: productGroupName,
        defaultTranslation: productGroupName,
        translation: productGroupTranslation && productGroupTranslation.translation || ''
      };
      productGroupsTranslations.push(mappedTranslation);
    }
  });
  return productGroupsTranslations;
};

export const getDynamicLanguageTemplate = async (dmsURL: string): Promise<Array<$LanguageTemplate>> => {
  const dms = new DMSConsumer(dmsURL);
  const activitiesResponse: $AxiosXHR<Array<$Activity>> = await dms.getActivities();
  const productGroupsResponse: $AxiosXHR<Array<$ProductGroup>> = await dms.getProductGroups();

  const activitiesTemplate: Array<$LanguageTemplate> = [];

  ActivityTypeGroups.forEach(group => {
    if(group.name && group.code) {
      activitiesTemplate.push({
        key: getActivityTypeTranslationCode(group.code || ''),
        name: group.name || '',
        defaultTranslation: group.name || ''
      })
    }
  });

  activitiesResponse && activitiesResponse.data && activitiesResponse.data.forEach((activity: $Activity) => {
    if (activity && activity.code && activity.name) {
      const { code, name } = activity;
      activitiesTemplate.push({
        key: getActivityTypeTranslationCode(code),
        name: name,
        defaultTranslation: name
      });
    }
  });

  const productGroupsTemplate: Array<$LanguageTemplate> = [];
  productGroupsResponse && productGroupsResponse.data && productGroupsResponse.data.forEach((group: $ProductGroup) => {
    if (group && group.code && group.name) {
      const { code, name } = group;
      productGroupsTemplate.push({
        key: getProductGroupTranslationCode(code),
        name: name,
        defaultTranslation: name
      });
    }
  });

  return [
    ...activitiesTemplate,
    ...productGroupsTemplate,
    ...TemplateTranslations
  ];
};
